import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Input,
  Button,
  Divider,
  Link,
  // useDisclosure,
  Chip
} from '@heroui/react';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  MapPin,
  AlertCircle,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import AreaSearch from '../AreaSearch';

const SignupPage = ({ onBackToLogin }) => {
  const [currentStep, setCurrentStep] = useState(1); // 1: location, 2: area confirmation, 3: user details
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  // Location and area state
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [area, setArea] = useState(null);
  const [newArea, setNewArea] = useState(null);
  const [cityName, setCityName] = useState('');
  const [stateName, setStateName] = useState('');
  const [selectedAreaFromSearch, setSelectedAreaFromSearch] = useState(null);
  const [showAreaSearch, setShowAreaSearch] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    passwordConfirm: '',
    orgCode: '',
    inviteCode: ''
  });

  //const { isOpen: isLocationOpen, onOpen: onLocationOpen, onOpenChange: onLocationOpenChange } = useDisclosure();

  // Check for invite code in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const inviteCode = urlParams.get('invite_code');
    if (inviteCode) {
      setFormData(prev => ({ ...prev, inviteCode }));
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const getCurrentLocation = () => {
    setIsLoading(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ lat: latitude, lng: longitude });
          fetchLocationDetails(latitude, longitude);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setError('Não foi possível obter sua localização. Você pode procurar sua área manualmente abaixo.');
          setShowAreaSearch(true);
          setIsLoading(false);
        }
      );
    } else {
      setError('Geolocalização não é suportada neste navegador. Você pode procurar sua área manualmente.');
      setShowAreaSearch(true);
      setIsLoading(false);
    }
  };

  const fetchLocationDetails = async (lat, lng) => {
    try {
      const key = 'AIzaSyDeDym7u3Hnf-WRB7Ci69jVG8hG20Cb0i4'; // Same key as old app
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${key}`
      );

      if (!response.ok) {
        throw new Error('Erro ao buscar detalhes da localização');
      }

      const data = await response.json();
      console.log('Google geocoding response:', data);

      let areaName = '';
      let areaType = '';
      let parentArea = '';
      let parentAreaType = '';
      let city = '';
      let state = '';

      // Parse Google's response (same logic as old app)
      for (let result of data.results) {
        for (let component of result.address_components) {
          if (component.types.includes('sublocality_level_2')) {
            areaName = component.long_name;
            areaType = 'sublocality_level_2';
          } else if (component.types.includes('sublocality_level_1')) {
            if (!areaName || areaType === 'administrative_area_level_2') {
              areaName = component.long_name;
              areaType = 'sublocality_level_1';
            } else if (areaName && areaType === 'sublocality_level_2') {
              parentArea = component.long_name;
              parentAreaType = 'sublocality_level_1';
            }
          } else if (component.types.includes('administrative_area_level_2')) {
            if (!areaName) {
              areaName = component.long_name;
              areaType = 'administrative_area_level_2';
            }
            city = component.long_name;
          } else if (component.types.includes('administrative_area_level_1')) {
            state = component.long_name;
          }
        }
      }

      if (city) {
        setCityName(city);
        setStateName(state);
        await fetchArea(areaName, areaType, city, state, lat, lng, parentArea, parentAreaType);
      } else {
        setError('Não foi possível determinar sua cidade.');
        setIsLoading(false);
      }

    } catch (error) {
      console.error('Error fetching location details:', error);
      setError('Erro ao buscar detalhes da localização.');
      setIsLoading(false);
    }
  };

  const fetchArea = async (areaName, areaType, city, state, lat, lng, parentArea, parentAreaType) => {
    try {
      const queryParams = new URLSearchParams({
        area: areaName,
        city: city,
        state: state
      });

      const response = await fetch(`/api/v1/areas?${queryParams}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Area search response:', data);

        if (data.areas && data.areas.length === 1) {
          // Area exists
          setArea(data.areas[0]);
          setCurrentStep(2);
        } else {
          // Area doesn't exist - will be created
          const newAreaData = {
            area_type: areaType,
            state: state,
            city: city,
            name: areaName,
            parent_area: parentArea,
            parent_area_type: parentAreaType
          };
          setNewArea(newAreaData);
          setCurrentStep(2);
        }
      } else {
        setError('Erro ao buscar informações da área.');
      }
    } catch (error) {
      console.error('Error fetching area:', error);
      setError('Erro ao buscar informações da área.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (formData.password !== formData.passwordConfirm) {
      setError('As senhas não coincidem');
      return;
    }

    if (!formData.firstName.trim() || !formData.lastName.trim() ||
        !formData.username.trim() || !formData.email.trim() ||
        !formData.password) {
      setError('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('username', formData.username);
      formDataToSend.append('first_name', formData.firstName);
      formDataToSend.append('last_name', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('password', formData.password);
      formDataToSend.append('password_confirm', formData.passwordConfirm);
      formDataToSend.append('lat', location.lat);
      formDataToSend.append('lon', location.lng);

      if (formData.orgCode) {
        formDataToSend.append('org_code', formData.orgCode);
      }

      if (formData.inviteCode) {
        formDataToSend.append('invite_code', formData.inviteCode);
      }

      if (area) {
        formDataToSend.append('area_id', area._id['$oid']);
      } else if (newArea) {
        formDataToSend.append('new_area', JSON.stringify(newArea));
      }

      const response = await fetch('/api/v1/users', {
        method: 'POST',
        credentials: 'include',
        body: formDataToSend
      });

      const data = await response.json();

      if (response.status === 404) {
        setError('O código de convite é inválido');
        return;
      }

      if (!response.ok) {
        if (data.requires_area_search) {
          // User is outside Brasil, need area search
          setCurrentStep(5); // New step for area search
          setError('');
          return;
        }

        if (data.validationError) {
          setError(data.validationError);
        } else if (data.error) {
          setError(data.error);
        } else {
          setError('Erro ao criar conta');
        }
        return;
      }

      // Success
      setSuccessMessage('Parabéns! Bem-vindo ao ugov. Comece a melhorar o nosso país.');
      setCurrentStep(4); // Success step

    } catch (error) {
      console.error('Signup error:', error);
      setError('Erro de rede. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAreaSelected = (selectedArea) => {
    setSelectedAreaFromSearch(selectedArea);
  };

  const completeRegistrationWithArea = async () => {
    if (!selectedAreaFromSearch) {
      setError('Por favor, selecione uma área');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Update user's area
      const response = await fetch('/api/v1/users/me/area', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          area_id: selectedAreaFromSearch.id
        })
      });

      if (response.ok) {
        setSuccessMessage('Área atualizada com sucesso! Bem-vindo ao ugov.');
        setCurrentStep(4); // Success step
      } else {
        const data = await response.json();
        setError(data.error || 'Erro ao atualizar área');
      }
    } catch (error) {
      console.error('Error updating area:', error);
      setError('Erro de rede. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: Location Selection
  if (currentStep === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
              <span className="text-white font-bold text-2xl">U</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Criar Conta</h1>
            <p className="text-gray-600 mt-2">
              Primeiro, vamos encontrar sua área
            </p>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-primary" />
                <span className="font-semibold">Localização</span>
              </div>
            </CardHeader>

            <CardBody className="space-y-4">
              {error && (
                <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-danger-600" />
                    <p className="text-danger-700 text-sm">{error}</p>
                  </div>
                </div>
              )}

              {!showAreaSearch ? (
                <div className="text-center space-y-4">
                  <p className="text-gray-600">
                    Para criar sua conta, precisamos saber em qual área você mora.
                  </p>

                  <Button
                    color="primary"
                    size="lg"
                    startContent={<MapPin className="w-5 h-5" />}
                    onPress={getCurrentLocation}
                    isLoading={isLoading}
                    className="w-full"
                  >
                    {isLoading ? 'Obtendo localização...' : 'Usar Minha Localização'}
                  </Button>

                  <div className="text-sm text-gray-500">ou</div>

                  <Button
                    variant="bordered"
                    size="lg"
                    onPress={() => setShowAreaSearch(true)}
                    className="w-full"
                  >
                    Procurar Área Manualmente
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-gray-600 mb-4">
                      Procure por sua cidade ou região no Brasil
                    </p>
                  </div>

                  <AreaSearch
                    onAreaSelected={(area) => {
                      setSelectedAreaFromSearch(area);
                      setArea({ _id: { '$oid': area.id }, name: area.name, user_count: 0 });
                      setCityName(area.city);
                      setStateName(area.state);
                      setCurrentStep(2);
                    }}
                    placeholder="Digite o nome da sua cidade..."
                  />

                  <Button
                    variant="light"
                    onPress={() => {
                      setShowAreaSearch(false);
                      setError('');
                    }}
                    className="w-full"
                  >
                    Voltar para Localização Automática
                  </Button>
                </div>
              )}

              <Divider />

              <div className="text-center">
                <Link
                  color="primary"
                  className="cursor-pointer"
                  onPress={onBackToLogin}
                  startContent={<ArrowLeft className="w-4 h-4" />}
                >
                  Voltar ao Login
                </Link>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  // Step 2: Area Confirmation
  if (currentStep === 2) {
    const areaDisplayName = area ? area.name : newArea?.name;
    const userCount = area ? area.user_count || 0 : 0;
    const isNewArea = !area;
    const fullAreaString = `${areaDisplayName}, ${cityName}, ${stateName}, Brasil`;

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <Card>
            <CardHeader>
              <div className="text-center w-full">
                <h2 className="text-2xl font-bold">
                  {isNewArea ? 'Parabéns! Você será o fundador!' : 'Área Encontrada!'}
                </h2>
                <p className="text-gray-600 mt-2">{fullAreaString}</p>
              </div>
            </CardHeader>

            <CardBody className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{areaDisplayName}</h3>
                    <p className="text-gray-600">{userCount} usuários</p>
                    {area?.created_at && (
                      <p className="text-sm text-gray-500">
                        Criada em {new Date(area.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <Chip
                      color={isNewArea ? "warning" : "success"}
                      variant="flat"
                    >
                      {isNewArea ? "Nova Área" : "Área Existente"}
                    </Chip>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <Button
                  variant="light"
                  onPress={() => setCurrentStep(1)}
                  startContent={<ArrowLeft className="w-4 h-4" />}
                >
                  Voltar
                </Button>
                <Button
                  color="primary"
                  className="flex-1"
                  onPress={() => setCurrentStep(3)}
                >
                  Continuar
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  // Step 3: User Details Form
  if (currentStep === 3) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader>
              <div className="text-center w-full">
                <h2 className="text-2xl font-bold">Dados Pessoais</h2>
                <p className="text-gray-600 mt-2">Complete seu cadastro</p>
              </div>
            </CardHeader>

            <CardBody className="space-y-4">
              {error && (
                <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-danger-600" />
                    <p className="text-danger-700 text-sm">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Primeiro Nome"
                    placeholder="João"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    variant="bordered"
                    isRequired
                  />
                  <Input
                    label="Sobrenome"
                    placeholder="Silva"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    variant="bordered"
                    isRequired
                  />
                </div>

                <Input
                  label="Nome de Usuário"
                  placeholder="joaosilva"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  startContent={<User className="w-4 h-4 text-gray-400" />}
                  variant="bordered"
                  isRequired
                />

                <Input
                  label="Email"
                  type="email"
                  placeholder="joao@email.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  startContent={<Mail className="w-4 h-4 text-gray-400" />}
                  variant="bordered"
                  isRequired
                />

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Senha"
                    placeholder="Sua senha"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    startContent={<Lock className="w-4 h-4 text-gray-400" />}
                    endContent={
                      <button
                        type="button"
                        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      >
                        {isPasswordVisible ? (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    }
                    type={isPasswordVisible ? "text" : "password"}
                    variant="bordered"
                    isRequired
                  />
                  <Input
                    label="Confirmar"
                    placeholder="Confirme"
                    value={formData.passwordConfirm}
                    onChange={(e) => handleInputChange('passwordConfirm', e.target.value)}
                    startContent={<Lock className="w-4 h-4 text-gray-400" />}
                    type={isPasswordVisible ? "text" : "password"}
                    variant="bordered"
                    isRequired
                  />
                </div>

                <Input
                  label="Código de Organização (opcional)"
                  placeholder="Código da sua organização"
                  value={formData.orgCode}
                  onChange={(e) => handleInputChange('orgCode', e.target.value)}
                  variant="bordered"
                />

                {formData.inviteCode && (
                  <Input
                    label="Código de Convite"
                    value={formData.inviteCode}
                    isReadOnly
                    variant="bordered"
                    color="success"
                  />
                )}

                <div className="flex space-x-3">
                  <Button
                    variant="light"
                    onPress={() => setCurrentStep(2)}
                    startContent={<ArrowLeft className="w-4 h-4" />}
                  >
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    className="flex-1"
                    isLoading={isLoading}
                  >
                    Criar Conta
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  // Step 5: Area Search (for users outside Brasil)
  if (currentStep === 5) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center pb-2">
              <h2 className="text-xl font-bold">Selecione sua Área</h2>
              <p className="text-sm text-gray-600 mt-2">
                Não encontramos sua localização em nosso sistema.
                Por favor, procure e selecione uma área no Brasil para continuar.
              </p>
            </CardHeader>
            <CardBody className="space-y-4">
              {error && (
                <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-danger-600" />
                    <p className="text-danger-700 text-sm">{error}</p>
                  </div>
                </div>
              )}

              <AreaSearch
                onAreaSelected={handleAreaSelected}
                placeholder="Procure por uma cidade ou região no Brasil..."
              />

              <div className="flex gap-3 pt-4">
                <Button
                  variant="bordered"
                  onPress={() => setCurrentStep(3)}
                  startContent={<ArrowLeft className="w-4 h-4" />}
                  className="flex-1"
                >
                  Voltar
                </Button>
                <Button
                  color="primary"
                  onPress={completeRegistrationWithArea}
                  isLoading={isLoading}
                  isDisabled={!selectedAreaFromSearch}
                  className="flex-1"
                >
                  {isLoading ? 'Finalizando...' : 'Finalizar Cadastro'}
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  // Step 4: Success
  if (currentStep === 4) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardBody className="text-center p-8">
              <div className="space-y-4">
                <CheckCircle className="w-16 h-16 text-success-500 mx-auto" />
                <h2 className="text-2xl font-bold text-success-700">Sucesso!</h2>
                <p className="text-gray-600">{successMessage}</p>
                <Button
                  color="primary"
                  size="lg"
                  className="w-full"
                  onPress={onBackToLogin}
                >
                  Fazer Login
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return null;
};

export default SignupPage;
